import type { CandidateExperience } from "@/lib/api/types";

export function formatMonths(totalMonths: number): string {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} yr${years !== 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} mos`);
  return parts.join(" ") || "< 1 mo";
}

export function computeDuration(
  start?: string | null,
  end?: string | null,
): string | null {
  if (!start) return null;
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  if (!endDate) return null;
  const months =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth());
  if (months < 0) return null;
  return formatMonths(months);
}

export function truncateSummaryText(text: string, max: number): string {
  if (text.length <= max) return text;
  let cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  if (lastSpace > max * 0.65) cut = cut.slice(0, lastSpace);
  return `${cut.trimEnd()}…`;
}

/** `*` denotes bullets: line-leading `* ` or single-line `intro * a * b` / `* a * b`. */
export function parseBulletBlocks(
  text: string,
): { type: "paragraph" | "list"; text?: string; items?: string[] }[] {
  const normalized = text.replace(/\r\n/g, "\n").trim();
  if (!normalized) return [];

  const lines = normalized.split("\n");
  const hasLineBullets = lines.some((l) => /^\s*\*\s/.test(l));

  if (hasLineBullets || lines.length > 1) {
    const blocks: {
      type: "paragraph" | "list";
      text?: string;
      items?: string[];
    }[] = [];
    let i = 0;
    while (i < lines.length) {
      if (lines[i].match(/^\s*\*\s/)) {
        const items: string[] = [];
        while (i < lines.length && lines[i].match(/^\s*\*\s/)) {
          const m = lines[i].match(/^\s*\*\s*(.*)$/);
          if (m) {
            const item = m[1].trim();
            if (item) items.push(item);
          }
          i++;
        }
        if (items.length) blocks.push({ type: "list", items });
      } else {
        const paraLines: string[] = [];
        while (i < lines.length && !lines[i].match(/^\s*\*\s/)) {
          paraLines.push(lines[i]);
          i++;
        }
        const p = paraLines.join("\n").trim();
        if (p) blocks.push({ type: "paragraph", text: p });
      }
    }
    return blocks;
  }

  const segments = normalized.split(/\s*\*\s+/);
  const first = segments[0]?.trim() ?? "";
  const rest = segments
    .slice(1)
    .map((s) => s.trim())
    .filter(Boolean);

  if (rest.length === 0) {
    return first ? [{ type: "paragraph", text: first }] : [];
  }

  if (normalized.startsWith("*")) {
    const allItems = segments.map((s) => s.trim()).filter(Boolean);
    return allItems.length ? [{ type: "list", items: allItems }] : [];
  }

  const blocks: {
    type: "paragraph" | "list";
    text?: string;
    items?: string[];
  }[] = [];
  if (first) blocks.push({ type: "paragraph", text: first });
  blocks.push({ type: "list", items: rest });
  return blocks;
}

export function groupExperiencesByCompany(experiences: CandidateExperience[]) {
  const groups: {
    companyId: string | null;
    companyName: string;
    companyDomain: string | null;
    companyIndustry: string | null;
    companySize: string | null;
    companyFounded: number | null;
    roles: CandidateExperience[];
  }[] = [];
  const companyMap = new Map<string, (typeof groups)[0]>();

  for (const exp of experiences) {
    const key = exp.company_id ?? exp.company_name?.toLowerCase() ?? "";
    if (companyMap.has(key)) {
      companyMap.get(key)!.roles.push(exp);
    } else {
      const group = {
        companyId: exp.company_id,
        companyName: exp.company_name ?? "",
        companyDomain: exp.company_domain,
        companyIndustry: exp.company_industry,
        companySize: exp.company_size,
        companyFounded: exp.company_founded,
        roles: [exp],
      };
      companyMap.set(key, group);
      groups.push(group);
    }
  }

  for (const g of groups) {
    g.roles.sort((a, b) => {
      const aCurrent = !a.end_date && a.start_date;
      const bCurrent = !b.end_date && b.start_date;
      if (aCurrent && !bCurrent) return -1;
      if (!aCurrent && bCurrent) return 1;
      const ta = a.start_date ? new Date(a.start_date).getTime() : 0;
      const tb = b.start_date ? new Date(b.start_date).getTime() : 0;
      return tb - ta;
    });
  }

  const primaryIdx = groups.findIndex((g) =>
    g.roles.some((r) => r.is_primary),
  );
  if (primaryIdx > 0) {
    const [primaryGroup] = groups.splice(primaryIdx, 1);
    groups.unshift(primaryGroup);
  }

  return groups;
}
